import * as Api from '@ViewModels';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const USER_QUERY_KEY = 'user';

export function useUserQuery({
	impersonationContext,
	id,
	enabled = true,
	refetchOnWindowFocus = false,
	meta,
}: {
	id: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		refetchOnWindowFocus,
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.IUser>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `user/${id}` }),
				'GET'
			);
		},
		queryKey: [USER_QUERY_KEY, id, impersonationContext],
		retry: 1,
		meta,
	});
}

export function useUserQueries({
	ids,
	impersonationContext,
}: {
	ids: string[];
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useQueries({
		queries: ids.map(id => ({
			queryFn: () => {
				return userSession.webServiceHelper.callAsync<Api.IUser>(
					Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: `user/${id}` }),
					'GET'
				);
			},
			queryKey: [USER_QUERY_KEY, id, impersonationContext],
		})),
	});
}

export function addUserToUserQueryCache({
	id,
	impersonationContext,
	user,
}: {
	id: string;
	impersonationContext?: Api.IImpersonationContext;
	user: Api.IUser;
}) {
	reactQueryClient.setQueryData([USER_QUERY_KEY, id, impersonationContext], user);
}
